import { useUnriddle } from '@/context/UnriddleContext';
import useAnalytics from '@/lib/analytics/useAnalytics';
import { api } from '@/lib/trpc/react';
import type { Transaction } from '@tiptap/pm/state';
import type { JSONContent } from 'novel';
import { useDebouncedCallback } from 'use-debounce';
import { useWorkspaceActions } from './useWorkspace';

export function useUpdateName(debounceTime = 1000) {
  const utils = api.useUtils();
  const { track } = useAnalytics();
  const { editor } = useUnriddle();
  const { mutate: updateNameMutation, isPending } =
    api.workspace.updateName.useMutation({
      onSuccess: (_, vars) => {
        track('update_name_navbar', {
          chatbotId: vars.id,
        });
      },
    });

  const debouncedUpdateNameMutation = useDebouncedCallback(
    (params: { id: string; name: string; type: 'CHAT' | 'CHATBOT' }) =>
      updateNameMutation(params),
    debounceTime,
  );

  const { updateChatbot } = useWorkspaceActions();

  const updateName = async (params: {
    id: string;
    name: string;
    type: 'CHAT' | 'CHATBOT';
    editorUpdate?: boolean;
  }) => {
    const currentChatbot = utils.workspace.all
      .getData()
      ?.entities.find((entity) => entity.id === params.id);
    const isNoteType = currentChatbot?.type === 'NOTE';

    updateChatbot(params.id, { name: params.name }, params.type);

    const editorName = params.name === 'Untitled' ? '' : params.name;

    utils.chatbot.byId.setData(params.id, (old) => {
      if (!old) return old;
      if (isNoteType && old.contents) {
        const contents = old.contents as JSONContent;
        if (contents.content && contents.content.length > 0) {
          const titleBlock = contents.content.find(
            (block) => block.type === 'title',
          );
          if (titleBlock) {
            titleBlock.content = [
              {
                type: 'text',
                text: editorName,
              },
            ];
          }
        }
        return {
          ...old,
          name: params.name,
          contents,
        };
      }

      return {
        ...old,
        name: params.name,
      };
    });

    if (isNoteType && editor && !params.editorUpdate) {
      // TODO: move the logic to a global state
      const titleNode = editor.state.doc.firstChild;
      if (titleNode && titleNode.type.name === 'title') {
        editor.commands.command(({ tr }: { tr: Transaction }) => {
          tr.setNodeMarkup(0, undefined, titleNode.attrs);
          tr.insertText(editorName, 1, titleNode.nodeSize - 1);
          return true;
        });
      }
    }

    debouncedUpdateNameMutation(params);
  };

  return { updateName, isRenaming: isPending };
}
